import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Skeleton } from '../components/ui/skeleton';
import { invoiceTemplateAPI } from '../lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Save, Loader2, CreditCard, FileText } from 'lucide-react';

export const AdminInvoiceTemplate = () => {
    const navigate = useNavigate();
    const [template, setTemplate] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => { load(); }, []);

    const load = async () => {
        try {
            const res = await invoiceTemplateAPI.get();
            setTemplate(res.data);
        } catch { toast.error('Failed to load invoice template'); }
        finally { setLoading(false); }
    };

    const handleChange = (field, value) => setTemplate(p => ({ ...p, [field]: value }));

    const handleSave = async () => {
        setSaving(true);
        try {
            await invoiceTemplateAPI.update(template);
            toast.success('Invoice template saved!');
        } catch { toast.error('Failed to save'); }
        finally { setSaving(false); }
    };

    if (loading) return (
        <Layout>
            <div className="space-y-4 max-w-2xl mx-auto">
                <Skeleton className="h-10 w-48" />
                <Skeleton className="h-48 rounded-xl" />
                <Skeleton className="h-48 rounded-xl" />
            </div>
        </Layout>
    );

    return (
        <Layout>
            <div className="space-y-4 max-w-2xl mx-auto">
                <div className="flex items-center gap-3">
                    <Button variant="ghost" size="icon" onClick={() => navigate('/settings')} className="h-10 w-10">
                        <ArrowLeft className="h-5 w-5" />
                    </Button>
                    <div className="flex-1">
                        <h1 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-900">Invoice Template</h1>
                        <p className="text-slate-500 text-sm">Payment terms, bank details & invoice T&Cs</p>
                    </div>
                </div>

                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                    Company branding (logo, name, address) is shared with the Quote Template and appears automatically on invoices.
                </div>

                {/* Payment Terms */}
                <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                        <CardTitle className="font-heading text-lg uppercase tracking-tight flex items-center gap-2">
                            <CreditCard className="h-5 w-5 text-red-600" />
                            Payment Terms
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Payment Terms</Label>
                            <Input
                                value={template?.payment_terms || ''}
                                onChange={e => handleChange('payment_terms', e.target.value)}
                                placeholder="Payment due on receipt"
                                className="h-12 border-slate-300"
                                data-testid="payment-terms-input"
                            />
                        </div>
                    </CardContent>
                </Card>

                {/* Bank Details */}
                <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                        <CardTitle className="font-heading text-lg uppercase tracking-tight flex items-center gap-2">
                            <CreditCard className="h-5 w-5 text-red-600" />
                            Bank Details
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Bank Name</Label>
                            <Input value={template?.bank_name || ''} onChange={e => handleChange('bank_name', e.target.value)}
                                placeholder="e.g. Lloyds Bank" className="h-12 border-slate-300" data-testid="bank-name-input" />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Sort Code</Label>
                                <Input value={template?.sort_code || ''} onChange={e => handleChange('sort_code', e.target.value)}
                                    placeholder="00-00-00" className="h-12 border-slate-300" data-testid="sort-code-input" />
                            </div>
                            <div className="space-y-2">
                                <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Account Number</Label>
                                <Input value={template?.account_number || ''} onChange={e => handleChange('account_number', e.target.value)}
                                    placeholder="00000000" className="h-12 border-slate-300" data-testid="account-number-input" />
                            </div>
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Payment Reference</Label>
                            <Input value={template?.payment_reference || ''} onChange={e => handleChange('payment_reference', e.target.value)}
                                placeholder="e.g. Invoice number or company name" className="h-12 border-slate-300" />
                        </div>
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">Invoice Notes</Label>
                            <Textarea value={template?.invoice_notes || ''} onChange={e => handleChange('invoice_notes', e.target.value)}
                                placeholder="e.g. Thank you for your business!" rows={2} className="border-slate-300" />
                        </div>
                    </CardContent>
                </Card>

                {/* Terms & Conditions */}
                <Card className="bg-white border-slate-200 shadow-sm">
                    <CardHeader className="pb-3">
                        <CardTitle className="font-heading text-lg uppercase tracking-tight flex items-center gap-2">
                            <FileText className="h-5 w-5 text-red-600" />
                            Terms &amp; Conditions
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <Textarea value={template?.terms_and_conditions || ''} onChange={e => handleChange('terms_and_conditions', e.target.value)}
                            rows={8} placeholder="Invoice-specific terms and conditions..." className="border-slate-300 font-mono text-sm" data-testid="invoice-tc-textarea" />
                    </CardContent>
                </Card>

                <Button onClick={handleSave} disabled={saving} className="w-full h-12 bg-red-600 hover:bg-red-700 text-white font-semibold uppercase tracking-wide" data-testid="save-invoice-template-btn">
                    {saving ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Saving...</> : <><Save className="mr-2 h-5 w-5" />Save Invoice Template</>}
                </Button>
            </div>
        </Layout>
    );
};
